import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Box, Button, CardContent, Divider, Grid, IconButton, Paper, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import { motion } from "framer-motion";

import { StatItem } from "./StatItems";
import CommunityStats from "./StatItems/CommunityStats";
import { getAPIUrl } from "./apiUtils";
import { formatDate } from "./utils";

const API_BASE_URL = getAPIUrl();

const SOURCE_TABS = [
  {
    id: "vk",
    label: "Вконтакте",
    sourceCountLabel: "Количество сообществ:",
    totalCountLabel: "Общее число постов:",
  },
  {
    id: "tg",
    label: "Telegram",
    sourceCountLabel: "Количество каналов:",
    totalCountLabel: "Общее число сообщений:",
  },
  {
    id: "news",
    label: "Новости",
    sourceCountLabel: "Количество источников:",
    totalCountLabel: "Общее число материалов:",
  },
  {
    id: "youtube",
    label: "YouTube",
    sourceCountLabel: "Количество каналов:",
    totalCountLabel: "Общее число видео:",
  },
];

const EMPTY_STATS = {
  totals: {
    source_count: 0,
    total_posts: 0,
    total_comments: 0,
    earliest_date: null,
    latest_date: null,
    updated_at: null,
  },
  groups: {},
};

function Home() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSource, setActiveSource] = useState("vk");
  const [statsBySource, setStatsBySource] = useState({});

  const activeSourceConfig = useMemo(
    () => SOURCE_TABS.find((source) => source.id === activeSource) || SOURCE_TABS[0],
    [activeSource]
  );

  const fetchSourceStats = useCallback(
    async (source) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/source_data/statistics`, {
          params: { source },
        });
        setStatsBySource((current) => ({
          ...current,
          [source]: response.data || EMPTY_STATS,
        }));
      } catch (fetchError) {
        console.error("Не удалось загрузить статистику источника", fetchError);
        setStatsBySource((current) => ({
          ...current,
          [source]: EMPTY_STATS,
        }));
        setError("Не удалось загрузить статистику");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchSourceStats(activeSource);
  }, [activeSource, fetchSourceStats]);

  const currentStats = statsBySource[activeSource] || EMPTY_STATS;
  const currentTotals = currentStats.totals || EMPTY_STATS.totals;
  const currentGroups = currentStats.groups || {};
  const hasActualStats = Object.keys(currentGroups).length > 0;

  const handleRefreshStats = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeSource === "vk") {
        await axios.post(`${API_BASE_URL}/vk/maintenance/refresh-vk-group-stats`);
      }
      await fetchSourceStats(activeSource);
    } catch (err) {
      console.error("Ошибка при отправке запроса на обновление:", err);
      setError("Не удалось запустить обновление");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: "10px", margin: "0 auto", backgroundColor: "#FFFFFF" }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: "16px",
                background: "linear-gradient(145deg, #ffffff, #e6f0fa)",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Tooltip
                      title={
                        currentTotals.updated_at
                          ? `Последнее обновление: ${formatDate(currentTotals.updated_at)}`
                          : "Дата обновления недоступна"
                      }
                      arrow
                    >
                      <IconButton sx={{ color: "#2563eb", mr: 1 }}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1e1e2f" }}>
                      Исходные данные
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefreshStats}
                    disabled={loading}
                  >
                    Обновить
                  </Button>
                </Box>

                <Tabs
                  value={activeSource}
                  onChange={(_, value) => setActiveSource(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 3, borderBottom: "1px solid #dbe3ef" }}
                >
                  {SOURCE_TABS.map((source) => (
                    <Tab key={source.id} value={source.id} label={source.label} />
                  ))}
                </Tabs>

                {error && (
                  <Box sx={{ mb: 2, p: 2, backgroundColor: "#ffebee", borderRadius: 1, border: "1px solid #f44336" }}>
                    <Typography variant="body2" sx={{ color: "#d32f2f" }}>
                      {error}
                    </Typography>
                  </Box>
                )}

                {loading && (
                  <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                    Загрузка статистики...
                  </Typography>
                )}

                {hasActualStats && (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={3} rowSpacing={1}>
                        <StatItem
                          label={activeSourceConfig.sourceCountLabel}
                          icon={<GroupIcon sx={{ color: "#2563eb", mr: 1 }} />}
                          value={currentTotals.source_count.toLocaleString()}
                        />
                        <StatItem label={activeSourceConfig.totalCountLabel} value={currentTotals.total_posts.toLocaleString()} />
                        <StatItem label="Общее число комментариев:" value={currentTotals.total_comments.toLocaleString()} />
                        <StatItem label="Самая ранняя дата:" value={formatDate(currentTotals.earliest_date)} />
                        <StatItem label="Самая поздняя дата:" value={formatDate(currentTotals.latest_date)} />
                      </Grid>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    <CommunityStats dataByRegion={currentGroups} />
                  </>
                )}

                {!loading && !hasActualStats && !error && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Нет данных для выбранного источника
                  </Typography>
                )}
              </CardContent>
            </Paper>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Home;
